import { useMemo } from "react";

import { Button } from "@similarweb/ui-components";

import { AddOnType, projectHookPreselect } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { translate } from "app/filters/i18nFilter";
import { GranularitySwitcher } from "app/pages/activation/components/GranularitySwitcher/GranularitySwitcher";
import { CountryIcon } from "app/pages/activation/components/Header/CountryIcon/CountryIcon";
import { Links } from "app/pages/activation/components/Header/Links/Links";
import { Loader } from "app/pages/activation/components/Header/Loader/Loader";
import { SharedIcon } from "app/pages/activation/components/Shared/SharedIcon/SharedIcon";
import { useSubscriptionContext } from "app/pages/activation/context/Subscription/SubscriptionProvider";
import { getExistingNames } from "app/pages/activation/helpers/getExistingNames";
import { selectSortedActivationSetList } from "app/pages/activation/helpers/home/header";
import { useFavicons } from "app/pages/activation/hooks/helpers/useFavicons";
import useDefaultActivation from "app/pages/activation/hooks/settings/useDefaultActivation";
import { useToggle } from "app/pages/activation/hooks/useToggle";
import { TABS_ARRAY } from "app/pages/activation/modules/Settings/config";
import { SettingsTabs } from "app/pages/activation/modules/Settings/types";
import { useShortActivationSetListQuery } from "app/pages/activation/queries/data/useShortActivationSetListQuery";
import { ACTIVATION_SETTINGS_ROUTE } from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import { ActivationSetDropdown } from "../ActivationSetDropdown/ActivationSetDropdown";
import ConfirmDefaultActivationModal from "../ConfirmDefaultActivationModal/ConfirmDefaultActivationModal";
import { SettingsIcon } from "../SettingsIcon/SettingsIcon";
import { SetupModal } from "../SetupModal/SetupModal";

import { ONE } from "./constants";
import { headerDataAutomation } from "./dataAutomationKeys";
import { ProjectBreadcrumbs } from "./ProjectBreadcrubs/ProjectBreadcrumbs";
import * as SC from "./styles";
import type { HeaderSchema } from "./type";

export const Header = ({
    domain,
    activationId,
    favIconUrl,
    isLoading,
    country,
    webSource,
    timeGranularity,
    isViewerMode = false,
    sharedBy,
    benchmarkId,
    defaultDuration,
}: HeaderSchema) => {
    const { isAlertsAvailable } = useSubscriptionContext();

    const { data: activationSetList } = useShortActivationSetListQuery(selectSortedActivationSetList(activationId));

    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const {
        setDefaultPage,
        setIsConfirmModalOpen,
        updateDefaultActivation,
        selectedActivationData,
        isConfirmModalOpen,
        defaultActivationId,
        isLoading: isDefaultActivation,
    } = useDefaultActivation();

    const { favicons, isIconsLoad } = useFavicons({ activationSetList });

    const [isOpen, onOpen, onClose] = useToggle();

    const isMultiple = ActivationComponentAssets.activationSetsLimitation > ONE;

    const { getIsAddonAvailable } = useAddonsState();

    const onCtaButtonClick = useMemo(() => {
        const canBuyAdditionalProjects = getIsAddonAvailable(AddOnType.AdditionalProjects);

        if (!canBuyAdditionalProjects) {
            return;
        }

        return () => {
            allTrackers.dispatchTrackEvent("internal link", "click", "project/add project");
            handlePurchaseAddonClick(projectHookPreselect);
        };
    }, [getIsAddonAvailable, handlePurchaseAddonClick]);

    const existingNames = useMemo(() => {
        if (activationSetList) {
            return getExistingNames(activationSetList);
        }
        return [];
    }, [activationSetList]);

    const currentProjectName = activationSetList.find((item) => {
        return item.id === activationId;
    })?.name;

    const onClickSettings = async () => {
        await proNavigator.go(ACTIVATION_SETTINGS_ROUTE, {
            key: activationId,
            country,
            webSource,
            timeGranularity,
            domain,
            tab: isViewerMode
                ? TABS_ARRAY.indexOf(SettingsTabs.Subscriptions)
                : TABS_ARRAY.indexOf(SettingsTabs.General),
        });

        activationTracking.home.trackSettingsOpen();
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <SC.HeaderWrapper data-automation={headerDataAutomation.wrapper}>
            <ProjectBreadcrumbs
                isLoading={isLoading}
                currentCrumbTitle={currentProjectName}
            />

            <SC.SectionWrapper>
                <SC.LeftTitleWrapper>
                    <SC.LinkWithTitle>
                        <ActivationSetDropdown
                            activationId={activationId}
                            isLoading={isLoading || isIconsLoad}
                            activationSetList={activationSetList}
                            favicons={favicons}
                            defaultIcon={favIconUrl}
                            isMultiple={isMultiple}
                            onOpen={onOpen}
                            setDefaultPage={setDefaultPage}
                            defaultPageId={defaultActivationId}
                        />
                    </SC.LinkWithTitle>

                    {isViewerMode ? <SharedIcon sharedBy={sharedBy} /> : null}

                    <CountryIcon country={country} />

                    <SettingsIcon onClick={onClickSettings} />
                </SC.LeftTitleWrapper>

                {onCtaButtonClick ? (
                    <Button
                        type="primary"
                        onClick={onCtaButtonClick}
                    >
                        {translate("activation.home.header.add_project_button_text")}
                    </Button>
                ) : null}
            </SC.SectionWrapper>

            <SC.SectionWrapper>
                <Links
                    benchmarkId={benchmarkId}
                    defaultDuration={defaultDuration}
                    activationId={activationId}
                    isAlertsAvailable={isAlertsAvailable}
                />

                <GranularitySwitcher timeGranularity={timeGranularity} />
            </SC.SectionWrapper>

            <SetupModal
                isOpen={isOpen}
                onClose={onClose}
                existingNames={existingNames}
                resetStateOnClose
            />

            <ConfirmDefaultActivationModal
                isOpen={isConfirmModalOpen}
                onUpdateDefaultActivation={updateDefaultActivation}
                onCloseClick={() => setIsConfirmModalOpen(false)}
                isLoading={isDefaultActivation}
                selectedName={selectedActivationData?.name}
            />
        </SC.HeaderWrapper>
    );
};
